import { useState, useEffect } from 'react';
import api from '../services/api';
import { useToast } from '../context/ToastContext';
import { Card, Btn, InfoAlert, PageHeader } from '../components/UI';

// ── Icons ─────────────────────────────────────────────────────────────────────
const IconMail    = ({ size = 15 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,12 2,6"/></svg>;
const IconUsers   = ({ size = 15 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
const IconSend    = ({ size = 15 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>;
const IconPreview = ({ size = 15 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>;
const IconBuilding = ({ size = 15 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><path d="M3 21h18M3 7l9-4 9 4M4 7v14M20 7v14M9 21V11h6v10"/></svg>;
const IconX       = ({ size = 13 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>;

const FILTER_OPTIONS = [
  { value: 'all',        label: 'All Users',       desc: 'Everyone with an account'        },
  { value: 'active',     label: 'Active',          desc: 'Accounts currently enabled'      },
  { value: 'inactive',   label: 'Inactive',        desc: 'Disabled accounts'               },
  { value: 'verified',   label: 'Verified',        desc: 'Email-verified accounts'         },
  { value: 'unverified', label: 'Unverified',      desc: 'Accounts pending verification'   },
];

// ── Recipient badge ───────────────────────────────────────────────────────────
function RecipientBadge({ filter, lodge, lodges, userCount }) {
  const opt         = FILTER_OPTIONS.find(f => f.value === filter);
  const lodgeName   = lodges.find(l => l.id === lodge)?.name;

  let desc = opt?.desc || '';
  if (lodgeName) desc += ` · ${lodgeName}`;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      background: 'var(--accent-dim)', border: '1px solid rgba(99,102,241,0.25)',
      borderRadius: 10, padding: '12px 16px',
    }}>
      <div style={{ width: 36, height: 36, borderRadius: 9, background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <IconUsers size={16} />
      </div>
      <div>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>
          {userCount !== null
            ? `${userCount.toLocaleString()} recipient${userCount !== 1 ? 's' : ''}`
            : 'Calculating…'}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{desc}</div>
      </div>
    </div>
  );
}

// ── Email preview ─────────────────────────────────────────────────────────────
function EmailPreview({ subject, message, filter, lodge, lodges }) {
  const filterLabel = FILTER_OPTIONS.find(f => f.value === filter)?.label || 'All Users';
  const lodgeName   = lodges.find(l => l.id === lodge)?.name;
  const audience    = lodgeName ? `${filterLabel} · ${lodgeName}` : filterLabel;

  return (
    <div style={{ background: '#0d0d14', borderRadius: 12, overflow: 'hidden', border: '1px solid #2a2a3a', fontSize: 13 }}>
      {/* Mock email chrome */}
      <div style={{ background: '#16161f', borderBottom: '1px solid #2a2a3a', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ display: 'flex', gap: 6 }}>
          {['#ff5f57','#febc2e','#28c840'].map(c => (
            <div key={c} style={{ width: 10, height: 10, borderRadius: '50%', background: c }} />
          ))}
        </div>
        <div style={{ flex: 1, background: '#0d0d14', borderRadius: 6, padding: '4px 10px', fontSize: 11, color: '#4a4a6a', fontFamily: 'monospace' }}>
          📧 Preview — {audience}
        </div>
      </div>

      <div style={{ padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
          <div style={{ background: '#6366f1', borderRadius: 8, padding: '6px 12px' }}>
            <span style={{ color: '#fff', fontSize: 12, fontWeight: 800, letterSpacing: '-0.02em' }}>&#x25CB; WiFi Portal</span>
          </div>
        </div>
        <div style={{ background: '#16161f', border: '1px solid #2a2a3a', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ background: 'linear-gradient(90deg,#6366f1,#818cf8)', height: 3 }} />
          <div style={{ padding: '24px 22px' }}>
            <p style={{ margin: '0 0 12px', fontSize: 11, fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Hi [User Name],</p>
            <h2 style={{ margin: '0 0 14px', fontSize: 18, fontWeight: 800, color: '#f0f0ff', letterSpacing: '-0.02em', lineHeight: 1.2 }}>
              {subject || <span style={{ color: '#33334a', fontStyle: 'italic' }}>Your subject line will appear here</span>}
            </h2>
            <div style={{ fontSize: 14, color: '#a0a0c0', lineHeight: 1.7, whiteSpace: 'pre-wrap', minHeight: 40 }}>
              {message || <span style={{ color: '#33334a', fontStyle: 'italic' }}>Your message will appear here…</span>}
            </div>
            <div style={{ marginTop: 20, borderTop: '1px solid #2a2a3a', paddingTop: 16 }}>
              <p style={{ margin: 0, fontSize: 11, color: '#33334a' }}>This message was sent by the WiFi Portal admin team.</p>
            </div>
          </div>
        </div>
        <p style={{ margin: '14px 0 0', fontSize: 11, color: '#33334a', textAlign: 'center' }}>
          © {new Date().getFullYear()} WiFi Portal · Sent to: <strong style={{ color: '#4a4a6a' }}>{audience}</strong>
        </p>
      </div>
    </div>
  );
}

// ── Result banner ─────────────────────────────────────────────────────────────
function ResultBanner({ result, onDismiss }) {
  if (!result) return null;
  const ok = result.failed === 0;
  return (
    <div style={{
      background: ok ? 'var(--green-dim)' : 'var(--yellow-dim)',
      border: `1px solid ${ok ? 'rgba(34,197,94,0.25)' : 'rgba(245,158,11,0.25)'}`,
      borderRadius: 10, padding: '14px 18px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
    }}>
      <div>
        <div style={{ fontWeight: 700, fontSize: 14, color: ok ? 'var(--green)' : 'var(--yellow)', marginBottom: 4 }}>
          {ok ? '✓ Broadcast complete!' : '⚠ Broadcast finished with errors'}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text2)' }}>
          {result.sent} sent · {result.failed} failed · {result.total} total recipients
        </div>
      </div>
      <button onClick={onDismiss}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', fontSize: 18, lineHeight: 1, padding: '0 4px' }}>×</button>
    </div>
  );
}

// ── Pill button ───────────────────────────────────────────────────────────────
function Pill({ active, onClick, children }) {
  return (
    <button onClick={onClick}
      style={{
        padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
        border: '1px solid', cursor: 'pointer', fontFamily: 'inherit',
        transition: 'all 0.12s',
        background: active ? 'var(--accent)' : 'transparent',
        color:      active ? '#fff' : 'var(--text3)',
        borderColor: active ? 'var(--accent)' : 'var(--border2)',
      }}>
      {children}
    </button>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function Broadcast() {
  // The sender was invisible here — it comes from SMTP_FROM in .env with the
  // display name taken from Site Settings, so surface the resolved value.
  const [sender, setSender] = useState(null);

  const toast = useToast();

  const [tab,       setTab]       = useState('broadcast'); // 'broadcast' | 'single'
  const [filter,    setFilter]    = useState('all');
  const [lodge,     setLodge]     = useState('');          // '' = all lodges
  const [lodges,    setLodges]    = useState([]);
  const [subject,   setSubject]   = useState('');
  const [message,   setMessage]   = useState('');
  const [preview,   setPreview]   = useState(false);
  const [busy,      setBusy]      = useState(false);
  const [result,    setResult]    = useState(null);
  const [userCount, setUserCount] = useState(null);

  // Single-user field
  const [toEmail, setToEmail] = useState('');

  // Fetch lodge list once
  useEffect(() => {
    api.getLodgesAdmin()
      .then(r => setLodges(r.lodges || []))
      .catch(() => {});
  }, []);

  // Resolve the sender so it is visible before anything is sent.
  useEffect(() => {
    api.getEmailSender().then(setSender).catch(() => {});
  }, []);

  // Recalculate recipient count when filter or lodge changes
  useEffect(() => {
    if (tab !== 'broadcast') return;
    setUserCount(null);
    setResult(null);

    const params = { limit: 1, page: 1 };
    if (filter === 'active')     params.status = 'active';
    if (filter === 'inactive')   params.status = 'inactive';
    if (filter === 'verified')   params.verified = 'true';
    if (filter === 'unverified') params.verified = 'false';
    if (lodge) params.lodge_id = lodge;

    api.getUsers(params)
      .then(r => setUserCount(r.total || 0))
      .catch(() => setUserCount('?'));
  }, [filter, lodge, tab]);

  const canSend = subject.trim() && message.trim() && (tab === 'single' ? toEmail.trim() : true);

  const handleBroadcast = async () => {
    if (!window.confirm(`Send to ${userCount} user${userCount !== 1 ? 's' : ''}? This cannot be undone.`)) return;
    setBusy(true);
    setResult(null);
    try {
      const body = { subject, message, filter };
      if (lodge) body.lodge_id = lodge;
      const r = await api.sendBulkEmail(body);
      setResult(r);
      toast(`Sent to ${r.sent} users`, 'success');
      setSubject('');
      setMessage('');
    } catch (e) {
      toast(e.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleSingle = async () => {
    setBusy(true);
    try {
      await api.sendEmail({ to: toEmail, subject, message });
      toast(`Email sent to ${toEmail}`, 'success');
      setSubject('');
      setMessage('');
      setToEmail('');
    } catch (e) {
      toast(e.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const selectedLodgeName = lodges.find(l => l.id === lodge)?.name;

  return (
    <div className="page-enter">
      <PageHeader
        title="Email"
        subtitle="Send messages to users directly from the admin panel"
      />

      {/* Who this actually comes from. The address is SMTP_FROM in the server
          .env (the mailbox must exist on the SMTP host); the display name and
          support address follow Site Settings. */}
      <InfoAlert>
        {sender ? (
          <>
            Sending as <strong>{sender.from}</strong>
            {sender.support_email ? <> · replies go to <strong>{sender.support_email}</strong></> : null}
            <div style={{ marginTop: 4, fontSize: 12, opacity: 0.8 }}>
              Display name comes from Site Settings → Site Name. The address is fixed
              by SMTP_FROM on the server and cannot be changed from here.
            </div>
          </>
        ) : 'Resolving sender identity…'}
      </InfoAlert>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid var(--border)' }}>
        {[
          { key: 'broadcast', label: 'Broadcast'   },
          { key: 'single',    label: 'Single User' },
        ].map(t => (
          <button key={t.key} onClick={() => { setTab(t.key); setResult(null); }}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
              padding: '9px 18px', fontSize: 13,
              fontWeight:  t.key === tab ? 700 : 400,
              color:       t.key === tab ? 'var(--accent)' : 'var(--text3)',
              borderBottom: `2px solid ${t.key === tab ? 'var(--accent)' : 'transparent'}`,
              marginBottom: -1, transition: 'all 0.12s',
            }}>
            {t.label}
          </button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: preview ? '1fr 1fr' : '1fr', gap: 16 }}>

        {/* ── Compose panel ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

          <ResultBanner result={result} onDismiss={() => setResult(null)} />

          {/* Recipient selector — broadcast only */}
          {tab === 'broadcast' && (
            <Card style={{ padding: '16px 18px' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 10 }}>
                Recipients
              </div>

              {/* Status filter pills */}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
                {FILTER_OPTIONS.map(opt => (
                  <Pill key={opt.value} active={filter === opt.value} onClick={() => setFilter(opt.value)}>
                    {opt.label}
                  </Pill>
                ))}
              </div>

              {/* Lodge filter */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 5 }}>
                  <IconBuilding size={11} />
                  Filter by Lodge
                </div>

                {lodges.length === 0 ? (
                  <div style={{ fontSize: 12, color: 'var(--text3)' }}>No lodges found.</div>
                ) : (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Pill active={lodge === ''} onClick={() => setLodge('')}>
                      All Lodges
                    </Pill>
                    {lodges.map(l => (
                      <Pill key={l.id} active={lodge === l.id} onClick={() => setLodge(l.id)}>
                        {l.name}
                        {l.user_count > 0 && (
                          <span style={{ marginLeft: 5, opacity: 0.7, fontSize: 10 }}>
                            ({l.user_count})
                          </span>
                        )}
                      </Pill>
                    ))}
                  </div>
                )}

                {/* Active lodge indicator */}
                {lodge && (
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    marginTop: 10, padding: '4px 10px',
                    background: 'var(--accent-dim)', borderRadius: 20,
                    border: '1px solid rgba(99,102,241,0.3)',
                  }}>
                    <IconBuilding size={11} />
                    <span style={{ fontSize: 11, color: 'var(--accent)', fontWeight: 600 }}>
                      {selectedLodgeName}
                    </span>
                    <button onClick={() => setLodge('')}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent)', padding: 0, display: 'flex', marginLeft: 2 }}>
                      <IconX size={10} />
                    </button>
                  </div>
                )}
              </div>

              {/* Recipient count badge */}
              <RecipientBadge filter={filter} lodge={lodge} lodges={lodges} userCount={userCount} />
            </Card>
          )}

          {/* Single-user recipient */}
          {tab === 'single' && (
            <Card style={{ padding: '16px 18px' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>
                Recipient Email
              </div>
              <input
                type="email"
                value={toEmail}
                onChange={e => setToEmail(e.target.value)}
                placeholder="user@example.com"
                style={{
                  width: '100%', padding: '10px 13px', borderRadius: 8,
                  border: '1px solid var(--border2)', background: 'var(--surface2)',
                  color: 'var(--text)', fontSize: 14, fontFamily: 'inherit',
                  outline: 'none', boxSizing: 'border-box',
                }}
              />
            </Card>
          )}

          {/* Compose */}
          <Card style={{ padding: '18px 18px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>Subject</div>
              <input
                value={subject}
                onChange={e => setSubject(e.target.value)}
                placeholder="e.g. Important update about your WiFi plan"
                style={{
                  width: '100%', padding: '10px 13px', borderRadius: 8,
                  border: '1px solid var(--border2)', background: 'var(--surface2)',
                  color: 'var(--text)', fontSize: 14, fontFamily: 'inherit',
                  outline: 'none', boxSizing: 'border-box',
                }}
              />
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>Message</div>
              <textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder="Write your message here. Plain text — keep it clear and concise."
                rows={10}
                style={{
                  width: '100%', padding: '10px 13px', borderRadius: 8,
                  border: '1px solid var(--border2)', background: 'var(--surface2)',
                  color: 'var(--text)', fontSize: 14, fontFamily: 'inherit',
                  resize: 'vertical', outline: 'none', boxSizing: 'border-box', lineHeight: 1.7,
                }}
              />
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 5 }}>
                {message.length} characters
              </div>
            </div>
          </Card>

          {/* Warning */}
          {tab === 'broadcast' && userCount > 0 && (
            <InfoAlert type="warning">
              ⚠ This will send to <strong>{userCount?.toLocaleString()} user{userCount !== 1 ? 's' : ''}</strong>
              {selectedLodgeName ? <> in <strong>{selectedLodgeName}</strong></> : ''}.
              Double-check your subject and message before sending.
            </InfoAlert>
          )}

          {tab === 'broadcast' && userCount === 0 && userCount !== null && (
            <InfoAlert type="info">
              No users match the current filter. Adjust your recipient selection.
            </InfoAlert>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <Btn variant="ghost" icon={IconPreview} onClick={() => setPreview(p => !p)}>
              {preview ? 'Hide Preview' : 'Preview'}
            </Btn>
            <Btn
              variant={tab === 'broadcast' ? 'warning' : 'primary'}
              icon={IconSend}
              loading={busy}
              disabled={!canSend || (tab === 'broadcast' && userCount === 0)}
              onClick={tab === 'broadcast' ? handleBroadcast : handleSingle}
            >
              {tab === 'broadcast' ? `Send to ${userCount ?? '…'} Users` : 'Send Email'}
            </Btn>
          </div>
        </div>

        {/* ── Preview panel ── */}
        {preview && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 10 }}>
              Live Preview
            </div>
            <EmailPreview subject={subject} message={message} filter={filter} lodge={lodge} lodges={lodges} />
          </div>
        )}
      </div>
    </div>
  );
}